import { clamp } from '@src/utils/functions/utils'
import { useEffect, useRef } from 'react'

export interface Options {
  completeOffsetPercentage: number // for :: top 0; center: .5; bottom: 1
  startOffsetPercentage: number // for :: top 1; center: .5; bottom: 0
  showOverflow: boolean
  degreeOfRotation: number
}

const getPercentagePassed = (element: HTMLElement, options?: Options) => {
  const { completeOffsetPercentage = 0, startOffsetPercentage = 0 } = options || {}
  const topOffSet = window.innerHeight * completeOffsetPercentage
  const bottomOffSet = window.innerHeight * startOffsetPercentage

  const reduceRootBy = topOffSet + bottomOffSet
  const heightOfVP = window.innerHeight - reduceRootBy
  const elementGapFromTopOfVP = (element.getBoundingClientRect().top || 0) - topOffSet

  const percentagePassed = clamp(elementGapFromTopOfVP / heightOfVP, 0, 1)
  return percentagePassed
}

const useScrollAnimation = <E extends HTMLElement, ImageElement extends HTMLElement>(
  addAnimation: (element: ImageElement, percentagePassed: number) => void,
  options?: Options,
) => {
  const wrapperRef = useRef<E>(null)
  const imageRef = useRef<ImageElement>(null)

  const refIsDefined = !!wrapperRef.current

  useEffect(() => {
    const handleIntersection: IntersectionObserverCallback = entries => {
      const [{ isIntersecting }] = entries
      let animationRunning: boolean
      const handleScroll = () => {
        if (wrapperRef.current) {
          const percentagePassed = getPercentagePassed(wrapperRef.current, options)
          if (animationRunning) return
          requestAnimationFrame(() => {
            if (wrapperRef.current && imageRef.current) addAnimation(imageRef.current, percentagePassed)
            animationRunning = false
          })
        }
      }
      if (isIntersecting) {
        document.addEventListener('scroll', handleScroll)
      }
    }
    const observer = new IntersectionObserver(handleIntersection)
    if (wrapperRef.current) observer.observe(wrapperRef.current)
  }, [refIsDefined, options, addAnimation])

  return { wrapperRef, imageRef }
}

export default useScrollAnimation
